import React, {ComponentType} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import {Collapse} from 'reactstrap';
import styled from 'styled-components';

import {HeadingText} from 'app/components/general/HeadingText';
import {gt} from 'app/utils/i18n';

interface FooterPropsAddedByStep<R = any> {
  isCompleted: boolean;
  result: R;
  text?: string;
  showSeparator?: boolean;
  canGoToPrevious?: boolean;
}

interface FooterPropsAddedByWizard<R = any> {
  onPrevious?: () => void;
  onFinish: (result: R) => unknown;
}

interface FooterComponentProps<R = any>
  extends FooterPropsAddedByStep<R>,
    FooterPropsAddedByWizard<R> {}
export interface WizardFormPaneProps<R = any> {
  FooterComponent: ComponentType<FooterPropsAddedByStep<R>>;
}

interface AccordionItem<R = any> {
  id: string;
  headerTitle: React.ReactNode;
  headerResult?: React.ReactNode;
  content: (
    FooterComponent: ComponentType<FooterPropsAddedByStep<R>>,
    onFinish: (result: R) => unknown
  ) => React.ReactNode;
  onFinish: (result: R) => unknown;
}

interface Props {
  active: string;
  steps: Array<AccordionItem>;
  onChange: (activeStepId: string) => unknown;
  inModal?: boolean;
}

export function WizardAccordion({
  active,
  steps,
  onChange,
  inModal,
}: Props): JSX.Element {
  const activeIndex = steps.map((i) => i.id).indexOf(active);

  const goToPrevious =
    activeIndex == 0 ? undefined : () => onChange(steps[activeIndex - 1].id);

  return (
    <StyledAccordionContinerDiv className={inModal ? 'mt-3' : ''}>
      {steps.map(
        ({id, headerTitle, headerResult = '', content, onFinish}, index) => {
          const status =
            index === activeIndex
              ? 'active'
              : index > activeIndex
              ? 'available'
              : 'completed';

          return (
            <div
              className={cs('accordion-item', {'in-modal': inModal})}
              key={id}
            >
              <h2 className="accordion-header">
                <button
                  onClick={() =>
                    status === 'active' || index > activeIndex
                      ? null
                      : onChange(id)
                  }
                  type="button"
                  aria-expanded={status === 'active' ? 'true' : 'false'}
                  className={cs('accordion-button', status, {
                    'with-value': headerResult != '',
                  })}
                >
                  <HeadingText
                    size="85%"
                    className="d-block"
                    style={{minWidth: 0}}
                  >
                    <span className="header-text d-flex align-items-center">
                      <span className="mr-3">{index + 1}</span>
                      <div style={{minWidth: 0}}>
                        <div className="header-title">{headerTitle}</div>
                        <div className="header-result text-truncate">
                          {headerResult}
                        </div>
                      </div>
                    </span>
                  </HeadingText>
                  <i className="completed-icon fas fa-check-circle ml-2"></i>
                </button>
              </h2>

              <Collapse mountOnEnter={true} isOpen={status === 'active'}>
                <div className="accordion-body">
                  {content(
                    (props) => (
                      <WizardAccordionStepFooter
                        {...props}
                        onPrevious={goToPrevious}
                        onFinish={onFinish}
                      />
                    ),
                    onFinish
                  )}
                </div>
              </Collapse>
            </div>
          );
        }
      )}
    </StyledAccordionContinerDiv>
  );
}

function WizardAccordionStepFooter<R>({
  isCompleted,
  result,
  onFinish,
  onPrevious,
  showSeparator = false,
  canGoToPrevious = true,
  text,
}: FooterComponentProps<R>): JSX.Element {
  return (
    <div
      className={cs(
        'd-flex mt-3 mr-auto',
        showSeparator ? 'border-top pt-3' : null
      )}
    >
      {onPrevious != null && canGoToPrevious ? (
        <button
          className="btn btn-outline-primary btn-sm px-3"
          onClick={() => onPrevious()}
        >
          <i className="fas fa-angle-left"></i> {gt('Previous')}
        </button>
      ) : null}
      <button
        className="btn btn-primary px-4 ml-auto"
        disabled={!isCompleted}
        onClick={() => onFinish(result)}
      >
        {text ?? gt('Next')} <i className="fas fa-angle-right"></i>
      </button>
    </div>
  );
}

const StyledAccordionContinerDiv = styled.div`
  .accordion-item {
    &:not(.in-modal) {
      &:first-of-type {
        border-top-left-radius: 0.3rem;
        border-top-right-radius: 0.3rem;
      }
    }

    &:not(:first-of-type) {
      border-top: 0;
    }

    &:last-of-type {
      border-bottom-left-radius: 0.3rem;
      border-bottom-right-radius: 0.3rem;

      .accordion-header {
        overflow: hidden;
        border-bottom-left-radius: 0.3rem;
        border-bottom-right-radius: 0.3rem;
      }
    }

    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.125);

    &.in-modal {
      border-left: none;
      border-right: none;
    }

    .accordion-header {
      margin-bottom: 0;

      .accordion-button {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: 0.75rem 1rem;
        font-size: 1rem;
        text-align: left;
        border: 0;
        border-radius: 0;
        overflow-anchor: none;
        transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
          border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out,
          border-radius 0.15s ease, padding 0.15s;

        &.completed {
          opacity: 0.75;
          color: ${colors.darkGreen};
          background-color: #e9f6e9;

          &.with-value {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;

            .header-text .header-title {
              font-size: 80%;
            }
          }

          .completed-icon {
            opacity: 1;
          }

          .header-text .header-result {
            opacity: 1;
            max-height: 1.2em;
          }
        }

        &.active {
          color: #0c63e4;
          background-color: #e7f1ff;
          box-shadow: inset 0 -1px 0 rgb(0 0 0 / 13%);

          .header-text {
            font-weight: bold;
          }
        }

        &.available {
          color: ${colors.linkBlue};
          background-color: #fafafa;
          cursor: default;
        }

        .header-text {
          font-weight: normal;
          transition: font-weight 0.15s;

          .header-title {
            transition: font-size 0.15s;
          }

          .header-result {
            overflow: hidden;
            opacity: 0;
            max-height: 0;
            transition: max-height 0.15s, opacity 0.15s;
          }
        }

        .completed-icon {
          color: ${colors.insGreenText};
          opacity: 0;
          transition: opacity 0.15s;
        }
      }
    }

    .accordion-body {
      padding: 1rem 1.25rem;
    }
  }
`;
